<template>
  <aside
    id="componentMoreOptions"
    v-trap-focus
    :aria-label="t('moreOptions.componentTitle')"
    class="flex options-container absolute h-auto justify-center items-center noselect"
    @keydown.esc="close()"
  >
    <ul
      id="moreOptionsList"
      :aria-label="t('moreOptions.listName')"
      class="flex flex-col self-start"
      @click="close()"
    >
      <FullscreenCommand
        v-if="attributesStore.isMobile"
        v-slot="{ disabled, icon, label }"
      >
        <IconText :title="label" :icon="icon" :disabled="disabled" />
      </FullscreenCommand>
      <ReadAloudCommand
        v-if="!wpsClient.token.showcaseTTS"
        v-slot="{ disabled, icon, label }"
      >
        <IconText
          :use-symbol="true"
          :title="label"
          :icon="icon"
          :disabled="disabled"
        />
      </ReadAloudCommand>
      <ShowGoToPositionCommand v-slot="{ disabled, icon, label }">
        <IconText :title="label" :icon="icon" :disabled="disabled" />
      </ShowGoToPositionCommand>
      <ShareCommand v-slot="{ disabled, icon, label }">
        <IconText :title="label" :icon="icon" :disabled="disabled" />
      </ShareCommand>
      <DownloadFileCommand v-slot="{ disabled, icon, label }">
        <IconText :title="label" :icon="icon" :disabled="disabled" />
      </DownloadFileCommand>
      <CopyLinkCommand v-slot="{ disabled, icon, label }">
        <IconText :title="label" :icon="icon" :disabled="disabled" />
      </CopyLinkCommand>
      <OfflineCommand v-slot="{ disabled, icon, label }">
        <IconText :title="label" :icon="icon" :disabled="disabled" />
      </OfflineCommand>
    </ul>
    <span
      id="anchorFocusMoreOptionsComponent"
      tabindex="0"
      aria-hidden="true"
    ></span>
  </aside>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

import IconText from "@/components/shared/IconText.vue";
import { trapFocus } from "@/helpers/trapFocus";
import { useAttributesStore } from "@/stores/attributes";

import { useWpsClient } from "@/wps/client";

import CopyLinkCommand from "@/commands/content/CopyLinkCommand.vue";
import DownloadFileCommand from "@/commands/content/DownloadFileCommand.vue";
import OfflineCommand from "@/commands/content/OfflineCommand.vue";
import ShareCommand from "@/commands/content/ShareCommand.vue";
import ReadAloudCommand from "@/commands/tts/ReadAloudCommand.vue";
import FullscreenCommand from "@/commands/ui/FullscreenCommand.vue";
import ShowGoToPositionCommand from "@/commands/ui/ShowGoToPositionCommand.vue";

import { useUILayers } from "../UILayers";

const { t } = useI18n();

const { close } = useUILayers();

const attributesStore = useAttributesStore();

const wpsClient = useWpsClient();

const vTrapFocus = {
  mounted: () => {
    document.addEventListener("keydown", keyBoardEvent);
    const anchorFocus = document.getElementById(
      "anchorFocusMoreOptionsComponent"
    );
    anchorFocus?.focus();
  },
  beforeUnmount: () => {
    document.removeEventListener("keydown", keyBoardEvent);
  },
};

function keyBoardEvent(event: KeyboardEvent) {
  const isTabPressed = event.key === "Tab";
  const modal = document.getElementById("componentMoreOptions");

  if (!isTabPressed || !modal) return;

  trapFocus(modal, event);
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;
.options-container {
  height: auto;
  overflow-y: auto;
  z-index: 3;
  right: $spacingM;
  top: $barHeight;
  padding: $spacingM 0;
  max-height: var(--safe-height);
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
  border: 3px solid rgba(158, 158, 158, 0.2);
}
.icon-disabled {
  color: var(--DM__color-grisFonce);
  opacity: 0.7;
  background-color: var(--DM__color-fill);
  cursor: not-allowed;
}
</style>
